import { IconMapPin, IconPhone, IconStarFilled } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, shopIsLong, valueFitScale } from '../posterParts';

/**
 * Landscape "Official" — the government-circular look on a wide sheet: a double ink border with corner blocks, the emblem medallion on
 * the left, the organisation and a ribbon in the middle, the vacancy count in a seal on the right, a 2×2 ruled table of facts and a
 * framed call-to-action. Palette: paper, paperDeep, ink, accent, ribbon, ribbonDark, text, danger.
 */
const SERIF = "'Noto Serif Bengali', 'Tiro Bangla', 'Hind Siliguri', serif";

function Ribbon({ p, children }) {
    return (
        <div className="relative mx-auto" style={{ width: '44cqw' }}>
            <svg viewBox="0 0 300 58" width="100%" style={{ display: 'block', height: 'auto' }} aria-hidden="true">
                <polygon points="0,12 40,12 40,54 0,54 14,33" fill={p.ribbonDark} />
                <polygon points="300,12 260,12 260,54 300,54 286,33" fill={p.ribbonDark} />
                <polygon points="22,42 40,54 40,42" fill="#000" fillOpacity="0.35" />
                <polygon points="278,42 260,54 260,42" fill="#000" fillOpacity="0.35" />
                <rect x="22" y="0" width="256" height="42" fill={p.ribbon} />
                <rect x="26" y="4" width="248" height="34" fill="none" stroke={p.accent} strokeWidth="1.2" strokeDasharray="3 2.2" />
            </svg>
            <div className="absolute left-[7.3%] right-[7.3%] top-0 flex items-center justify-center" style={{ height: '72.4%' }}>{children}</div>
        </div>
    );
}

function Cell({ p, label, last = false, children }) {
    return (
        <div style={{ padding: `${fs(1.3)} ${fs(2)}`, borderRight: last ? 'none' : `0.2cqw solid ${hexToRgba(p.ink, 0.5)}` }}>
            <div style={{ color: p.ink, fontSize: fs(1.8), fontWeight: 800, lineHeight: 1.2, opacity: 0.85 }}>{label}</div>
            <div style={{ color: p.text, marginTop: fs(0.5) }}>{children}</div>
        </div>
    );
}

export default function LandscapeOfficialDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 60);
    const ageScale = valueFitScale(f.age, 40);

    const body = (
        <div className="flex flex-1 flex-col justify-center" style={{ padding: `${fs(4.4)} 6.6cqw ${fs(2)}`, gap: fs(1.8) }}>
            <div className="grid items-center" style={{ gridTemplateColumns: '16cqw 1fr 17cqw', gap: fs(2) }}>
                <div className="flex justify-center">
                    <div className="flex items-center justify-center rounded-full" style={{ width: fs(13), height: fs(13), background: '#fff', border: `${fs(0.45)} solid ${p.accent}`, boxShadow: `0 0 0 ${fs(0.4)} ${p.paper}, 0 0 0 ${fs(0.7)} ${p.ink}` }}>
                        <PosterLogo job={job} size={10} />
                    </div>
                </div>
                <div className="flex min-w-0 flex-col items-center text-center" style={{ gap: fs(1) }}>
                    <div className="flex items-center justify-center" style={{ gap: fs(1.2) }}>
                        <div style={{ height: '0.2cqw', width: '10cqw', background: `linear-gradient(90deg, transparent, ${p.accent})` }} />
                        <div style={{ width: fs(1.1), height: fs(1.1), background: p.accent, transform: 'rotate(45deg)' }} />
                        <div style={{ height: '0.2cqw', width: '10cqw', background: `linear-gradient(270deg, transparent, ${p.accent})` }} />
                    </div>
                    <div style={{ color: p.ink, fontSize: fs(4.3), fontWeight: 800, lineHeight: 1.18, textShadow: '0 0.2cqw 0 rgba(255,255,255,0.75)' }}>{f.org}</div>
                    {f.position && <div style={{ color: p.text, opacity: 0.85, fontFamily: "'Tiro Bangla', 'Noto Serif Bengali', serif", fontStyle: 'italic', fontSize: fs(2.3), lineHeight: 1.3 }}>{f.position}</div>}
                    <Ribbon p={p}>
                        <div style={{ color: '#fff', fontSize: fs(2.6), fontWeight: 800, letterSpacing: '0.03em', textShadow: '0 0.2cqw 0.3cqw rgba(0,0,0,0.35)' }}>{f.label.notice}</div>
                    </Ribbon>
                </div>
                <div className="flex justify-center">
                    <div className="flex flex-col items-center justify-center rounded-full text-center" style={{ width: fs(15), height: fs(15), background: '#fff', border: `${fs(0.45)} solid ${p.accent}`, boxShadow: `0 0 0 ${fs(0.4)} ${p.paper}, 0 0 0 ${fs(0.7)} ${p.ink}` }}>
                        <div style={{ color: p.ink, fontSize: fs(1.7), fontWeight: 800, lineHeight: 1 }}>{f.label.vacancy}</div>
                        <div style={{ color: p.danger, fontSize: `calc(${6.2 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1.05 }}>{f.vacancy}</div>
                        <div style={{ color: p.ink, fontSize: fs(2), fontWeight: 800, lineHeight: 1 }}>{f.label.unit}</div>
                    </div>
                </div>
            </div>

            <div className="overflow-hidden" style={{ border: `0.3cqw solid ${p.ink}`, borderRadius: '0.7cqw', background: 'rgba(255,255,255,0.55)', boxShadow: '0 0.4cqw 1cqw rgba(0,0,0,0.12)' }}>
                <div className="grid" style={{ gridTemplateColumns: '1.9fr 1fr' }}>
                    <Cell p={p} label={f.label.edu}>
                        <div style={{ fontSize: `calc(${2.5 * eduScale}cqw * var(--fs, 1))`, fontWeight: 700, lineHeight: 1.28 }}>{f.edu}</div>
                    </Cell>
                    <Cell p={p} label={f.label.age} last>
                        <div style={{ fontSize: `calc(${2.5 * ageScale}cqw * var(--fs, 1))`, fontWeight: 700, lineHeight: 1.28 }}>{f.age}</div>
                    </Cell>
                </div>
                <div className="grid" style={{ gridTemplateColumns: '1.9fr 1fr', borderTop: `0.2cqw solid ${hexToRgba(p.ink, 0.5)}` }}>
                    <Cell p={p} label={f.label.start}>
                        <div style={{ fontSize: fs(2.6), fontWeight: 800 }}>{f.start}</div>
                    </Cell>
                    <Cell p={p} label={f.label.end} last>
                        <div style={{ display: 'inline-block', background: p.danger, color: '#fff', borderRadius: '99px', padding: `${fs(0.2)} ${fs(1.8)}`, fontSize: fs(2.6), fontWeight: 800 }}>{f.end}</div>
                    </Cell>
                </div>
            </div>

            <div style={{ border: `0.25cqw solid ${p.ink}`, borderRadius: '1cqw', padding: '0.5cqw' }}>
                <div className="flex items-center justify-center" style={{ background: p.ink, borderRadius: '0.6cqw', color: '#fff', padding: `${fs(1.2)} ${fs(2)}`, gap: fs(1.8), fontSize: fs(2.8), fontWeight: 800 }}>
                    <IconStarFilled style={{ width: fs(1.8), height: fs(1.8), color: p.accent }} />
                    <div>{f.label.cta}</div>
                    <IconStarFilled style={{ width: fs(1.8), height: fs(1.8), color: p.accent }} />
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="flex items-center justify-between" style={{ margin: '0 3.7cqw 3.7cqw', borderTop: `0.6cqw double ${p.ink}`, background: hexToRgba(p.ink, 0.08), padding: `${fs(1.4)} 3cqw`, gap: `${fs(0.8)} ${fs(2)}`, flexWrap: shopIsLong(f.shop) ? 'wrap' : 'nowrap' }}>
            <div className="flex min-w-0 items-center" style={{ gap: fs(1.6) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(5.2), height: fs(5.2), background: '#fff', border: `0.3cqw solid ${p.accent}`, padding: '0.4cqw' }} />}
                <div style={{ color: p.ink, fontSize: fs(3.2), fontWeight: 800, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex min-w-0 items-center justify-center" style={{ flex: shopIsLong(f.shop) ? '1 0 100%' : '1 1 0%', order: shopIsLong(f.shop) ? 3 : 0, gap: fs(0.8), color: p.text, fontSize: fs(2.1), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 1 }}>
                <IconMapPin className="flex-none" style={{ width: fs(2.4), height: fs(2.4), color: p.accent }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex flex-none items-center" style={{ gap: fs(0.8), color: p.ink, fontSize: fs(2.9), fontWeight: 800 }}>
                    <IconPhone className="flex-none" style={{ width: fs(2.7), height: fs(2.7), color: p.accent }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    const corner = (pos) => (
        <div className="absolute" style={{ ...pos, width: '2.6cqw', height: '2.6cqw', background: p.paper, border: `0.4cqw solid ${p.ink}` }}>
            <div style={{ position: 'absolute', inset: '0.5cqw', background: p.accent, transform: 'rotate(45deg) scale(0.7)' }} />
        </div>
    );
    const overlay = (
        <>
            <div className="pointer-events-none absolute" style={{ inset: '1.3cqw', border: `0.6cqw solid ${p.ink}`, borderRadius: '0.4cqw', opacity: 0.92 }} />
            <div className="pointer-events-none absolute" style={{ inset: '2.6cqw', border: `0.18cqw solid ${p.ink}`, opacity: 0.75 }} />
            {corner({ left: '0.1cqw', top: '0.1cqw' })}
            {corner({ right: '0.1cqw', top: '0.1cqw' })}
            {corner({ left: '0.1cqw', bottom: '0.1cqw' })}
            {corner({ right: '0.1cqw', bottom: '0.1cqw' })}
        </>
    );

    return (
        <PosterSheet
            landscape
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={SERIF}
            background={`radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.65), transparent 60%), linear-gradient(180deg, ${p.paper} 0%, ${p.paperDeep} 100%)`}
            body={body}
            footer={footer}
            overlay={overlay}
        />
    );
}
